// Der Verlauf der Akte in Worten (E3, S5; F16: wer hat wann was gemacht).
// Der Server schreibt nur Kürzel – eine Art („gesendet“, „geaendert“ …) und
// die Tabelle, an der es geschah (server/src/verlauf.ts; nie Inhalte). Hier
// wird daraus ein Satz für Menschen: „Besichtigung an das Büro gesendet“,
// „Kunde berichtigt“. Reine Logik – der Test prüft jede Art, die es am
// Server gibt (verlauf.test.ts liest server/src/verlauf.ts mit).
//
// Unbekanntes bleibt ehrlich: eine Art, die hier (noch) fehlt, heißt
// „Änderung“ – lieber ein schlichtes Wort als ein falsches.

import type { AkteVerlaufEintrag } from "@happy-energy/kern";

/** Wenn eine Art hier nicht vorkommt. */
export const UNBEKANNT = "Änderung";

/** Was ein Datensatz für Menschen ist. */
const DING: Record<string, string> = {
  kunde: "Kunde",
  objekt: "Objekt",
  anlage: "Anlage",
  aufnahme: "Besichtigung",
  nachtrag: "Nachtrag",
  materialliste: "Materialliste",
  frage: "Frage",
  antwort: "Antwort",
  station: "Station",
  foto: "Foto",
  notiz: "Notiz",
  sprachmemo: "Sprachmemo",
  laenge: "Länge",
  nutzer: "Zugang",
  geraet: "Gerät",
  einladung: "Einladung",
  sitzung: "Anmeldung",
  firma: "Firma",
  // Seit E3c, S2: die Bestellung beim Lieferanten und der Einsatz einer Partie (Dach oder Boden).
  bestellung: "Bestellung",
  einsatz: "Partie",
};

function ding(tabelle: string): string {
  return DING[tabelle] ?? "Eintrag";
}

/**
 * Der Satz zu einem Eintrag im Verlauf – je Art und Tabelle. Die Arten
 * stehen in server/src/verlauf.ts (`VerlaufArt`); was dort dazukommt, gehört
 * auch hierher (der Test merkt es).
 */
export function verlaufText(e: Pick<AkteVerlaufEintrag, "was" | "tabelle">): string {
  const { was, tabelle } = e;
  switch (was) {
    case "gesendet":
      return tabelle === "aufnahme" ? "Besichtigung an das Büro gesendet" : `${ding(tabelle)} gesendet`;
    case "nachgereicht":
      return "Nachtrag gesendet";
    case "freigegeben":
      return tabelle === "aufnahme" ? "Material freigegeben – die Materialliste steht" : `${ding(tabelle)} freigegeben`;
    case "gesehen":
      if (tabelle === "aufnahme") return "Nachtrag oder neue Dachplanung gesehen (Büro)";
      // Bis E3c drückte das Lager „gesehen“ an der Materialliste – die Einträge bleiben im Verlauf.
      if (tabelle === "materialliste") return "Änderung der Materialliste gesehen";
      return `${ding(tabelle)} gesehen`;
    case "geaendert":
      switch (tabelle) {
        case "kunde":
          return "Kunde berichtigt";
        case "objekt":
          return "Adresse berichtigt";
        case "anlage":
          // Seit E3c (S3) auch die Module und der Excel-Report von K2.
          return "Anlage geändert (Dachplanung, Termine oder Module)";
        case "materialliste":
          return "Material nach der Freigabe geändert";
        default:
          return `${ding(tabelle)} geändert`;
      }
    case "angelegt":
      switch (tabelle) {
        case "aufnahme":
          return "Besichtigung begonnen";
        case "nachtrag":
          return "Nachtrag begonnen";
        case "nutzer":
          return "Zugang angelegt (eingeladen)";
        default:
          return `${ding(tabelle)} angelegt`;
      }
    case "geloescht":
      return `${ding(tabelle)} gelöscht`;
    case "gefragt":
      return "Frage gestellt";
    case "geantwortet":
      return "Frage beantwortet";
    case "frage-erledigt":
      return "Frage erledigt";
    case "frage-wieder-offen":
      return "Frage wieder offen";
    case "rolle-geaendert":
      return "Rolle geändert";
    case "schon-erledigt":
      return `${ding(tabelle)}: als schon erledigt vermerkt (ohne System)`;
    case "vor-dem-update":
      return `${ding(tabelle)}: vor dem Update erledigt – beim Update übernommen`;
    // Seit E3c, S3 und S4: Bestellung und Partien.
    case "bestellt":
      return "Bestellt (die Mail an den Lieferanten ist hinaus)";
    case "beauftragt":
      return "Dachpartie beauftragt (die Mail ist hinaus)";
    case "eingeteilt":
      return "Bodenpartie eingeteilt (die Unterlage ist gedruckt)";
    case "wieder-offen":
      return `${ding(tabelle)} wieder offen – nach dem Abhaken hat sich etwas geändert`;
    case "weggelassen":
      return "Wechselrichter und Speicher kommen doch nicht dazu";
    case "angemeldet":
      return "Angemeldet";
    case "abgemeldet":
      return "Abgemeldet";
    case "passkey-eingerichtet":
      return "Passkey eingerichtet";
    case "einladung-erstellt":
      return "Einladungscode erstellt";
    case "einladung-eingeloest":
      return "Einladungscode eingelöst";
    case "geraet-angelegt":
      return "Neues Gerät angemeldet";
    case "geraet-freigegeben":
      return "Gerät freigegeben";
    case "geraet-gesperrt":
      return "Gerät gesperrt";
    case "nutzer-gesperrt":
      return "Zugang gesperrt (ausgetreten)";
    case "nutzer-entsperrt":
      return "Zugang wieder aufgenommen";
    default:
      return UNBEKANNT;
  }
}

/**
 * Wer – mit dem Gerät in Klammern: „Max Muster (iPhone Verkauf)“. Ist der
 * Zugang nicht mehr da: ehrlich „nicht mehr bekannt“. Was das Update selbst
 * übernommen hat (seit E3c, S2), hat keine Person: „beim Update“.
 */
export function verlaufWer(e: Pick<AkteVerlaufEintrag, "wer" | "geraet"> & { was?: string }): string {
  if (e.wer === null && e.was === "vor-dem-update") return "beim Update";
  const wer = e.wer?.name ?? "nicht mehr bekannt";
  return e.geraet ? `${wer} (${e.geraet})` : wer;
}

/** Wie das Ding zu sehen ist – eine Farbe je Art: was von draußen kam, was das Büro tat, Fragen. */
export type VerlaufArt = "draussen" | "buero" | "frage" | "sonst";

export function verlaufArt(e: Pick<AkteVerlaufEintrag, "was" | "tabelle">): VerlaufArt {
  if (e.was === "gefragt" || e.was === "geantwortet" || e.was.startsWith("frage-")) return "frage";
  if (e.was === "gesendet" || e.was === "nachgereicht") return "draussen";
  if (
    ["freigegeben", "gesehen", "schon-erledigt", "bestellt", "beauftragt", "eingeteilt"].includes(e.was) ||
    (e.was === "geaendert" && ["kunde", "objekt", "materialliste"].includes(e.tabelle))
  ) {
    return "buero";
  }
  return "sonst";
}
